/**
 * Date helpers using date-fns.
 * Display format for dates: "Jan 01, 2001" (MMM dd, yyyy).
 */
import {
  format as formatFns,
  formatDistanceToNow,
  isAfter,
  isBefore,
  isToday,
  isTomorrow,
  isYesterday,
  isValid,
  parseISO,
  startOfDay,
  subDays,
} from 'date-fns'

/** Display format for date only: e.g. "Jan 01, 2001" */
export const DISPLAY_DATE_FORMAT = 'MMM dd, yyyy'

/** Format for API/inputs: YYYY-MM-DD */
export const ISO_DATE_FORMAT = 'yyyy-MM-dd'

/** Format for time: e.g. "3:45 PM" */
export const DISPLAY_TIME_FORMAT = 'h:mm a'

/** Format for date + time: e.g. "Jan 01, 2001, 3:45 PM" */
export const DISPLAY_DATETIME_FORMAT = "MMM dd, yyyy, h:mm a"

/**
 * Format a Date or ISO string for display (date only): "Jan 01, 2001".
 * @param {Date|string|null|undefined} date - Date instance or ISO 8601 string
 * @returns {string} Formatted date or empty string
 */
export function formatDisplayDate(date) {
  if (date == null) return ''
  const d = typeof date === 'string' ? parseISO(date) : date
  if (!isValid(d)) return ''
  return formatFns(d, DISPLAY_DATE_FORMAT)
}

/**
 * Format a Date or ISO string for display (date + time): "Jan 01, 2001, 3:45 PM".
 * @param {Date|string|null|undefined} date - Date instance or ISO 8601 string
 * @returns {string} Formatted datetime or empty string
 */
export function formatDisplayDateTime(date) {
  if (date == null) return ''
  const d = typeof date === 'string' ? parseISO(date) : date
  if (!isValid(d)) return ''
  return formatFns(d, DISPLAY_DATETIME_FORMAT)
}

/**
 * Format time only: "3:45 PM".
 * @param {Date|string|null|undefined} date - Date instance or ISO 8601 string
 * @returns {string} Formatted time or empty string
 */
export function formatDisplayTime(date) {
  if (date == null) return ''
  const d = typeof date === 'string' ? parseISO(date) : date
  if (!isValid(d)) return ''
  return formatFns(d, DISPLAY_TIME_FORMAT)
}

/**
 * Get YYYY-MM-DD for a date (e.g. for API or input[type="date"]).
 * @param {Date|string|null|undefined} date - Date instance or ISO 8601 string
 * @returns {string} yyyy-MM-dd or empty string
 */
export function formatYmd(date) {
  if (date == null) return ''
  const d = typeof date === 'string' ? parseISO(date) : date
  if (!isValid(d)) return ''
  return formatFns(d, ISO_DATE_FORMAT)
}

/**
 * Today's date as YYYY-MM-DD.
 * @returns {string}
 */
export function todayYmd() {
  return formatYmd(new Date())
}

/**
 * Parse an ISO 8601 string to a Date. Returns invalid Date if input is invalid.
 * @param {string} iso - ISO 8601 string
 * @returns {Date}
 */
export { parseISO }

/**
 * Check if a value is a valid Date.
 * @param {*} d
 * @returns {boolean}
 */
export { isValid as isValidDate }

/**
 * Re-export common date-fns helpers for use in the app.
 */
export {
  formatDistanceToNow,
  isAfter,
  isBefore,
  isToday,
  isTomorrow,
  isYesterday,
  startOfDay,
  subDays,
}
